export type PlaybackTimelineSample = {
  tSec: number;
  capturedAt: string;
  capturedAtMs: number;
  latitude?: number;
  longitude?: number;
  frame?: number;
};

export type PlaybackTimeline = {
  schemaVersion?: number;
  timezone?: string;
  durationSec?: number;
  sampleIntervalSec?: number;
  samples: PlaybackTimelineSample[];
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return value;
};

export const normalizePlaybackTimeline = (value: unknown): PlaybackTimeline | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const payload = value as Record<string, unknown>;
  const rawSamples = Array.isArray(payload.samples) ? payload.samples : [];
  const samples = rawSamples
    .map((entry): PlaybackTimelineSample | null => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const sample = entry as Record<string, unknown>;
      const tSec = toFiniteNumber(sample.t_sec ?? sample.tSec);
      const capturedAt = typeof sample.captured_at === 'string'
        ? sample.captured_at
        : typeof sample.capturedAt === 'string'
          ? sample.capturedAt
          : null;
      const capturedAtMs = capturedAt ? new Date(capturedAt).getTime() : NaN;
      if (tSec == null || !capturedAt || !Number.isFinite(capturedAtMs)) {
        return null;
      }
      const frame = toFiniteNumber(sample.frame);
      const latitude = toFiniteNumber(sample.latitude ?? sample.lat);
      const longitude = toFiniteNumber(sample.longitude ?? sample.lng ?? sample.lon);
      return {
        tSec,
        capturedAt,
        capturedAtMs,
        latitude: latitude ?? undefined,
        longitude: longitude ?? undefined,
        frame: frame ?? undefined,
      };
    })
    .filter((sample): sample is PlaybackTimelineSample => sample !== null)
    .sort((a, b) => a.tSec - b.tSec);

  if (samples.length === 0) {
    return null;
  }

  return {
    schemaVersion: toFiniteNumber(payload.schema_version ?? payload.schemaVersion) ?? undefined,
    timezone: typeof payload.timezone === 'string' ? payload.timezone : undefined,
    durationSec: toFiniteNumber(payload.duration_sec ?? payload.durationSec) ?? undefined,
    sampleIntervalSec: toFiniteNumber(payload.sample_interval_sec ?? payload.sampleIntervalSec) ?? undefined,
    samples,
  };
};

const findUpperBoundByTime = (samples: PlaybackTimelineSample[], targetMs: number) => {
  let lo = 0;
  let hi = samples.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (samples[mid].capturedAtMs < targetMs) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
};

const findUpperBoundByT = (samples: PlaybackTimelineSample[], targetSec: number) => {
  let lo = 0;
  let hi = samples.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (samples[mid].tSec < targetSec) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
};

const interpolate = (x: number, x0: number, y0: number, x1: number, y1: number) => {
  if (x1 === x0) {
    return y0;
  }
  const ratio = (x - x0) / (x1 - x0);
  return y0 + (y1 - y0) * ratio;
};

export const resolvePlaybackSecondAtTimeMs = (
  timeline: PlaybackTimeline | null | undefined,
  targetMs: number | null | undefined,
): number | null => {
  if (!timeline || targetMs == null || !Number.isFinite(targetMs)) {
    return null;
  }
  const { samples } = timeline;
  if (samples.length === 0) {
    return null;
  }
  if (targetMs <= samples[0].capturedAtMs) {
    return samples[0].tSec;
  }
  const last = samples[samples.length - 1];
  if (targetMs >= last.capturedAtMs) {
    return last.tSec;
  }
  const upperIndex = findUpperBoundByTime(samples, targetMs);
  const lower = samples[Math.max(upperIndex - 1, 0)];
  const upper = samples[upperIndex];
  return interpolate(targetMs, lower.capturedAtMs, lower.tSec, upper.capturedAtMs, upper.tSec);
};

export const resolveTimeMsAtPlaybackSecond = (
  timeline: PlaybackTimeline | null | undefined,
  targetSec: number | null | undefined,
): number | null => {
  if (!timeline || targetSec == null || !Number.isFinite(targetSec)) {
    return null;
  }
  const { samples } = timeline;
  if (samples.length === 0) {
    return null;
  }
  if (targetSec <= samples[0].tSec) {
    return samples[0].capturedAtMs;
  }
  const last = samples[samples.length - 1];
  if (targetSec >= last.tSec) {
    return last.capturedAtMs;
  }
  const upperIndex = findUpperBoundByT(samples, targetSec);
  const lower = samples[Math.max(upperIndex - 1, 0)];
  const upper = samples[upperIndex];
  return interpolate(targetSec, lower.tSec, lower.capturedAtMs, upper.tSec, upper.capturedAtMs);
};

export const resolvePositionAtPlaybackSecond = (
  timeline: PlaybackTimeline | null | undefined,
  targetSec: number | null | undefined,
): { latitude: number; longitude: number } | null => {
  if (!timeline || targetSec == null || !Number.isFinite(targetSec)) {
    return null;
  }
  const samples = timeline.samples.filter(
    (sample) =>
      typeof sample.latitude === 'number' &&
      Number.isFinite(sample.latitude) &&
      typeof sample.longitude === 'number' &&
      Number.isFinite(sample.longitude),
  );
  if (samples.length === 0) {
    return null;
  }
  if (targetSec <= samples[0].tSec) {
    return { latitude: samples[0].latitude as number, longitude: samples[0].longitude as number };
  }
  const last = samples[samples.length - 1];
  if (targetSec >= last.tSec) {
    return { latitude: last.latitude as number, longitude: last.longitude as number };
  }
  const upperIndex = findUpperBoundByT(samples, targetSec);
  const lower = samples[Math.max(upperIndex - 1, 0)];
  const upper = samples[upperIndex];
  return {
    latitude: interpolate(targetSec, lower.tSec, lower.latitude as number, upper.tSec, upper.latitude as number),
    longitude: interpolate(targetSec, lower.tSec, lower.longitude as number, upper.tSec, upper.longitude as number),
  };
};
